import { useCallback, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { useAppDispatch } from '@/store'
import { debounce } from '@/utils'

import { selectVehiclesMapStyles, setCustomMapStyle } from '@/features/domain/ui'
import { selectIsTelematicsUnavailable } from '@/features/domain/account'

export function useVisibility(id: string) {
  const dispatch = useAppDispatch()
  const mapStyles = useSelector(selectVehiclesMapStyles)
  const isTelematicsUnavailable = useSelector(selectIsTelematicsUnavailable)
  const [loading, setLoading] = useState(false)
  const loadingRef = useRef(false)

  const globalStyle = mapStyles.mode
  const customStyle = mapStyles.custom[id]
  const mode = customStyle || globalStyle

  // Create the debounced function only when dependencies change
  const debouncedToggleVisibility = useMemo(
    () =>
      debounce(async (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation()
        if (loadingRef.current) return

        loadingRef.current = true
        setLoading(true)

        try {
          await dispatch(
            setCustomMapStyle({
              customStyle: { type: 'vehicle', mode: mode !== 'off' ? 'off' : 'on', ids: [id] },
            }),
          )
        } finally {
          loadingRef.current = false
          setLoading(false)
        }
      }, 200),
    [dispatch, id, mode],
  )

  // Callback that invokes the memoized debounced function
  const toggleVisibility = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (isTelematicsUnavailable) return
      debouncedToggleVisibility(e)
    },
    [debouncedToggleVisibility, isTelematicsUnavailable],
  )

  return [mode !== 'off', toggleVisibility, loading, isTelematicsUnavailable] as const
}
